import { ChevronIcon } from '../Common';

export interface PaginationProps {
  setPage: (page: number) => void;
  totalPages?: number;
  page: number;
  compact?: boolean;
}

const generateValidPageNumbers = (leftMostPage: number, rightMostPage: number, max: number) => {
  const start = Math.max(leftMostPage, 1);
  const end = Math.min(rightMostPage, max);
  return Array.from({ length: end - start + 1 }, (_, index) => index + start);
};

export const Pagination = ({ setPage, totalPages, page: currentPage, compact }: PaginationProps) => {
  if (!totalPages) return;
  let leftMostPage = currentPage - 2;
  let rightMostPage = currentPage + 2;
  const carryToRight = 1 - leftMostPage;
  const carryToLeft = rightMostPage - totalPages;

  if (carryToLeft > 0) {
    leftMostPage -= carryToLeft;
  }
  if (carryToRight > 0) {
    rightMostPage += carryToRight;
  }

  const validPageNumbers = generateValidPageNumbers(leftMostPage, rightMostPage, totalPages);

  const navClass = compact ? 'flex justify-center py-1' : 'flex justify-center mt-7 mb-1 lg:mt-14';
  const gapClass = compact ? 'flex flex-row gap-1' : 'flex flex-row gap-3 lg:gap-8';
  const chevronClass = compact ? 'w-3 h-3' : 'w-4 h-4 lg:w-6 lg:h-6';
  const btnClass = compact ? 'h-6 w-6 text-xs' : 'h-8 w-8 lg:h-10 lg:w-10 text-sm lg:text-lg';
  const textColor = compact ? 'text-[#999] hover:text-[#E8E8E8]' : 'text-white';
  const ellipsisClass = compact ? 'text-xs text-[#666]' : 'text-lg font-normal';

  return (
    <nav className={navClass}>
      <div className={gapClass}>
        {currentPage > 1 && (
          <button onClick={() => setPage(currentPage - 1)} aria-label="Goto previous page" className={`flex items-center justify-center ${compact ? `${btnClass} ${textColor}` : ''}`}>
            <ChevronIcon className={`${chevronClass} transform rotate-90`} />
          </button>
        )}
        {validPageNumbers[0] > 1 && (
          <span className={`${ellipsisClass} flex items-center justify-center px-1`}>...</span>
        )}
        {validPageNumbers.map((number) => (
          <button
            key={number}
            className={`flex ${btnClass} items-center justify-center rounded-full font-normal
              ${number === currentPage ? `bg-[#C3D82E]/20 ${compact ? 'text-[#E8E8E8]' : 'text-white'}` : textColor}`}
            onClick={() => setPage(number)}
            tabIndex={0}
            aria-label={`Go to page ${number}`}
          >
            {number}
          </button>
        ))}
        {validPageNumbers[validPageNumbers.length - 1] < totalPages && (
          <span className={`${ellipsisClass} flex items-center justify-center px-1`}>...</span>
        )}
        {currentPage < totalPages && (
          <button onClick={() => setPage(currentPage + 1)} aria-label="Goto next page" className={`flex items-center justify-center ${compact ? `${btnClass} ${textColor}` : ''}`}>
            <ChevronIcon className={`${chevronClass} transform -rotate-90`} />
          </button>
        )}
      </div>
    </nav>
  );
};
